/**
 * ModalitySemanticDetector — generic categorical-state → semantic verdict.
 *
 * Generalises the head-pose "옆 반복 응시" (repeated side-gaze) pipeline to
 * any single modality whose per-tick output is a categorical STATE
 * (gaze-area zone, held-object class, body-lean direction …). It turns a
 * stream of states into the final human-readable verdict that a modality
 * reports — e.g. "왼쪽 영역 반복 응시 (3회)" or "휴대폰 파지 지속 (3.2s)".
 *
 * Three-stage pipeline (the caller logs each stage with a timestamp):
 *   1. raw    — the per-tick state itself (logged by the caller on change).
 *   2. window — qualified ENTRIES into an alert state accumulate inside a
 *               rolling window; `entryAdded` flags the tick a new entry was
 *               counted so the caller can emit a "누적 N회" window log.
 *   3. verdict— a semantic conclusion: either `sustain` (held in one alert
 *               state ≥ sustainMs) or `repeat` (re-entered the same alert
 *               state ≥ minRepeat times within the window).
 *
 * A non-baseline state must be HELD ≥ minHoldMs before it counts as an
 * entry — this debounces single-frame flicker (consistent with the
 * temporal-consistency approach used elsewhere in the demo) so a stray
 * classification never inflates the repeat count.
 */

export type VerdictKind = 'none' | 'sustain' | 'repeat';

export interface SemanticVerdict {
  kind: VerdictKind;
  /** Dominant alert state behind the verdict (null when kind = 'none'). */
  state: string | null;
  /** Re-entry count within the window (meaningful for kind = 'repeat'). */
  count: number;
  /** Continuous dwell in the current alert state, ms (for kind = 'sustain'). */
  sustainedMs: number;
  /** A verdict is currently asserted (kind !== 'none'). */
  active: boolean;
  /** True only on the tick a new qualified entry was counted (window stage). */
  entryAdded: boolean;
  /** Running entry count for the current alert state this tick. */
  entryCount: number;
}

export interface ModalitySemanticConfig {
  /** States treated as normal — they assert no verdict and reset dwell. */
  baseline: string[];
  /** Rolling window for repeat counting (ms). */
  windowMs?: number;
  /** Re-entries within the window to call it a 'repeat'. */
  minRepeat?: number;
  /** Continuous dwell to call it a 'sustain' (ms). */
  sustainMs?: number;
  /** Min dwell before a non-baseline state counts as an entry (debounce). */
  minHoldMs?: number;
}

export class ModalitySemanticDetector {
  private baseline: Set<string>;
  private windowMs: number;
  private minRepeat: number;
  private sustainMs: number;
  private minHoldMs: number;

  private curState: string | null = null;
  private curSince = 0;
  private counted = false;             // current dwell already counted?
  private entries = new Map<string, number[]>();  // per-state entry timestamps

  constructor(cfg: ModalitySemanticConfig) {
    this.baseline = new Set(cfg.baseline);
    this.windowMs = cfg.windowMs ?? 12_000;
    this.minRepeat = cfg.minRepeat ?? 3;
    this.sustainMs = cfg.sustainMs ?? 2_500;
    this.minHoldMs = cfg.minHoldMs ?? 250;
  }

  update(state: string, now: number): SemanticVerdict {
    // State transition → restart the dwell clock.
    if (state !== this.curState) {
      this.curState = state;
      this.curSince = now;
      this.counted = false;
    }

    const isAlert = !this.baseline.has(state);
    const sustainedMs = isAlert ? now - this.curSince : 0;

    // Window stage — count a qualified entry once the alert state has been
    // held long enough to be real (debounce), recorded at most once/dwell.
    let entryAdded = false;
    if (isAlert && !this.counted && sustainedMs >= this.minHoldMs) {
      const arr = this.entries.get(state) ?? [];
      arr.push(now);
      this.entries.set(state, arr);
      this.counted = true;
      entryAdded = true;
    }

    // Prune all entry rings to the rolling window.
    const cutoff = now - this.windowMs;
    for (const [k, arr] of Array.from(this.entries.entries())) {
      while (arr.length && arr[0] < cutoff) arr.shift();
      if (arr.length === 0) this.entries.delete(k);
    }

    const entryCount = isAlert ? (this.entries.get(state)?.length ?? 0) : 0;

    // Verdict stage — sustain takes priority while actively dwelling;
    // otherwise the strongest repeat across alert states.
    let kind: VerdictKind = 'none';
    let vState: string | null = null;
    let vCount = 0;

    if (isAlert && sustainedMs >= this.sustainMs) {
      kind = 'sustain';
      vState = state;
      vCount = entryCount;
    } else {
      let bestState: string | null = null;
      let bestCount = 0;
      for (const [k, arr] of Array.from(this.entries.entries())) {
        if (arr.length > bestCount) { bestCount = arr.length; bestState = k; }
      }
      if (bestState && bestCount >= this.minRepeat) {
        kind = 'repeat';
        vState = bestState;
        vCount = bestCount;
      }
    }

    return {
      kind,
      state: vState,
      count: vCount,
      sustainedMs,
      active: kind !== 'none',
      entryAdded,
      entryCount,
    };
  }

  reset(): void {
    this.curState = null;
    this.curSince = 0;
    this.counted = false;
    this.entries.clear();
  }
}
